import { call, put, take } from "redux-saga/effects";
import { Toastify } from "../../components/Toaster";
import { httpDelete, httpGet, httpPost, httpPut } from "../../services";
import { buildAuditLogRequest } from "./auditLogsQueryParams";

import { GetConsumerAuditLogsArgs, GetConsumerAuditLogsSchema } from "../actions/customerManagementActions";
import { setAppFeaturesAction } from "../actions/platformConfigurationActions";
import {
  DeleteSamlConfigSchema,
  GetAuditLogsSchema,
  GetMapFieldsSchema,
  GetRoleListSchema,
  InviteTeamMemberPayload,
  OwnerInfoDataSchema,
  RoleSchema,
  SamlConfigSchema,
  TeamConst,
  TransferAccountSchema,
  deleteSamlConfigErrorAction,
  deleteSamlConfigLoadingAction,
  deleteSamlConfigSuccessAction,
  deleteSelfErrorAction,
  deleteSelfLoadingAction,
  deleteSelfSuccessAction,
  deleteTeamUserErrorAction,
  deleteTeamUserLoadingAction,
  deleteTeamUserSuccessAction,
  getAppRolesErrorAction,
  getAppRolesLoadingAction,
  getAppRolesSuccessAction,
  getAuditLogsErrorAction,
  getAuditLogsLoadingAction,
  getAuditLogsSuccessAction,
  getCurrentUserErrorAction,
  getCurrentUserLoadingAction,
  getCurrentUserSuccessAction,
  getMapFieldsErrorAction,
  getMapFieldsLoadingAction,
  getMapFieldsSuccessAction,
  getOwnerInfoErrorAction,
  getOwnerInfoLoadingAction,
  getOwnerInfoSuccessAction,
  getRoleListErrorAction,
  getRoleListLoadingAction,
  getRoleListSuccessAction,
  getSamlConfigAction,
  getSamlConfigErrorAction,
  getSamlConfigLoadingAction,
  getSamlConfigSuccessAction,
  getTeamAuditLogsV2ErrorAction,
  getTeamAuditLogsV2LoadingAction,
  getTeamAuditLogsV2SuccessAction,
  getTeamOwnerErrorAction,
  getTeamOwnerLoadingAction,
  getTeamOwnerSuccessAction,
  getTeamUsersErrorAction,
  getTeamUsersLoadingAction,
  getTeamUsersSuccessAction,
  inviteTeamMemberErrorAction,
  inviteTeamMemberLoadingAction,
  inviteTeamMemberSuccessAction,
  logOutTeamUserErrorAction,
  logOutTeamUserLoadingAction,
  logOutTeamUserSuccessAction,
  postSamlConfigErrorAction,
  postSamlConfigLoadingAction,
  postSamlConfigSuccessAction,
  putSamlConfigErrorAction,
  putSamlConfigLoadingAction,
  putSamlConfigSuccessAction,
  resendTeamUserPasswordErrorAction,
  resendTeamUserPasswordLoadingAction,
  resendTeamUserPasswordSuccessAction,
  transferAccountErrorAction,
  transferAccountLoadingAction,
  transferAccountSuccessAction,
  updateCurrentUserErrorAction,
  updateCurrentUserLoadingAction,
  updateCurrentUserSuccessAction,
  updateTeamUserRoleErrorAction,
  updateTeamUserRoleLoadingAction,
  updateTeamUserRoleSuccessAction,
  wasUserAddedInActiveSessionErrorAction,
  wasUserAddedInActiveSessionLoadingAction,
  wasUserAddedInActiveSessionSuccessAction
} from "../actions/teamActions";

/**
 * Workers
 */

function* getTeamUsersHandler(): any {
  try {
    yield put(getTeamUsersLoadingAction());
    const response = yield httpGet("/team/users");
    verifySuccessOfResponse("Get Team User", response);
    for (let i = 0; i < response.length; i++) {
      response[i].Email = typeof response[i].Email === "string" ? response[i].Email : response[i].Email[0].Value;
    }
    yield put(getTeamUsersSuccessAction({ response }));
  } catch (e) {
    yield put(getTeamUsersErrorAction(e.message));
  }
}

function verifySuccessOfResponse(request: string, requestResponse: any) {
  if (requestResponse.lrmessage !== undefined) {
    throw Error(`${request}: ${requestResponse.lrmessage}`);
  } else if (requestResponse.ErrorCode !== undefined) {
    throw Error(`${request}: ${requestResponse.Description || requestResponse.Message}`);
  }
}

function* getTeamOwnerHandler(): any {
  try {
    yield put(getTeamOwnerLoadingAction());
    const data = yield httpGet("/team/owner");
    verifySuccessOfResponse("Get Team Owner", data);
    if (data.ErrorCode && data.ErrorCode === 403) {
      yield put(getTeamOwnerSuccessAction({ ...data, response: {} }));
    } else {
      yield put(getTeamOwnerSuccessAction({ response: data }));
    }
  } catch (e) {
    yield put(getTeamOwnerErrorAction(e.message));
  }
}

function* deleteTeamUserHandler(uid: string): any {
  try {
    yield put(deleteTeamUserLoadingAction());
    const data = yield httpDelete("/team/user", {}, { uid });
    data.userDeleted = uid;
    yield put(deleteTeamUserSuccessAction({ response: data }));
  } catch (e) {
    yield put(deleteTeamUserErrorAction(e.message));
  }
}

function* deleteSelfHandler(appID: number, appName: string): any {
  try {
    yield put(deleteSelfLoadingAction());
    const data = yield httpDelete("/account/leave-tenant", {}, { appList: [{ appID, AppName: appName }] });
    if (data.response.message && data.response.message.length) {
      let err = "";
      for (const msg of data.response.message) {
        if (msg.action === "Error") {
          err = msg.message;
          break;
        }
      }
      if (err) {
        throw new Error(err);
      }
    }
    yield put(deleteSelfSuccessAction({ response: data }));
  } catch (e) {
    yield put(deleteSelfErrorAction(e.message));
  }
}

function* resendTeamUserPasswordHandler(email: string, template: string, url: string): any {
  try {
    yield put(resendTeamUserPasswordLoadingAction());
    const response = yield httpPost("/team/verifyemail", {}, { Email: email, template, url });
    response.email = email;
    yield put(resendTeamUserPasswordSuccessAction({ response }));
  } catch (e) {
    yield put(resendTeamUserPasswordErrorAction(e.message));
  }
}

function* logOutTeamUserHandler(uid: string): any {
  try {
    yield put(logOutTeamUserLoadingAction());
    const response: any = yield httpPost("/team/invalidate/all", {}, { uid });
    yield put(logOutTeamUserSuccessAction({ response }));
  } catch (e) {
    yield put(logOutTeamUserErrorAction(e.message));
  }
}

function* getCurrentUserHandler(): any {
  try {
    yield put(getCurrentUserLoadingAction());
    const data = yield httpGet("/team/currentuser");
    verifySuccessOfResponse("Get Current User", data);
    yield put(getCurrentUserSuccessAction({ response: data }));
  } catch (e) {
    yield put(getCurrentUserErrorAction(e.message));
  }
}

function* getAppRolesHandler(): any {
  try {
    yield put(getAppRolesLoadingAction());
    const data = yield httpGet("/team/roles");
    verifySuccessOfResponse("Get App Roles", data);
    yield put(getAppRolesSuccessAction({ response: data }));
  } catch (e) {
    yield put(getAppRolesErrorAction(e.message));
  }
}

function* updateTeamUserRoleHandler(email: string, roles: RoleSchema[], additionalPermissions: string[]): any {
  try {
    yield put(updateTeamUserRoleLoadingAction());
    const data = yield httpPut(
      "/team/role",
      {},
      {
        email: email,
        siteList: {
          role: roles,
          additionalpermission: additionalPermissions
        }
      }
    );
    if (data.ErrorCode) {
      throw new Error(data.Message);
    }

    yield put(updateTeamUserRoleSuccessAction({ response: data }));
  } catch (e) {
    let message = e.message;
    if (e.status === 401) message = e.message;
    yield put(updateTeamUserRoleErrorAction(message));
  }
}

function* getAuditLogsHandler(cursor: number): any {
  try {
    yield put(getAuditLogsLoadingAction());

    const data: GetAuditLogsSchema = yield httpGet("/audit-logs/auditlogbyappid", {
      cursor
    });
    if (data["description"]) {
      throw new Error(data["description"]);
    }
    yield put(getAuditLogsSuccessAction({ ...data }));
  } catch (e) {
    yield put(getAuditLogsErrorAction(e.message));
  }
}

function* getTeamAuditLogsV2Handler(args: GetConsumerAuditLogsArgs = {}): any {
  try {
    yield put(getTeamAuditLogsV2LoadingAction());

    const { query, body } = buildAuditLogRequest(args);

    const data: GetConsumerAuditLogsSchema = yield httpPost("/audit-logs/v2/team", query, body);

    yield put(getTeamAuditLogsV2SuccessAction({ ...data }));
  } catch (e) {
    yield put(getTeamAuditLogsV2ErrorAction(e.message));
  }
}

function* getRoleListHandler(): any {
  try {
    yield put(getRoleListLoadingAction());

    const roles: GetRoleListSchema = yield httpGet("/team/roles");
    const allRoles: GetRoleListSchema[] = [];

    if (roles && roles.data) {
      roles.data.forEach(function (role) {
        if (role.Name !== "SuperUser_ReadOnly" && role.Name !== "SuperUser_Write" && role.Name !== "RootAdmin_Write" && role.Name !== "Owner") {
          allRoles.push({
            id: role.Name,
            permission: role.Permissions
          });
        }
      });
    }

    yield put(getRoleListSuccessAction(allRoles));
  } catch (e) {
    yield put(getRoleListErrorAction(e.message));
  }
}

function* getOwnerInfoHandler() {
  try {
    yield put(getOwnerInfoLoadingAction());

    const data: OwnerInfoDataSchema = yield httpGet("/team/owner");

    yield put(getOwnerInfoSuccessAction({ ...data }));
  } catch (e) {
    yield put(getOwnerInfoErrorAction(e.message));
  }
}

function* inviteTeamMemberHandler(payload: InviteTeamMemberPayload): any {
  try {
    yield put(inviteTeamMemberLoadingAction());
    const data = yield httpPost("/team/member", {}, payload);
    if (data.ErrorCode) {
      throw new Error(data.Message);
    }
    yield put(inviteTeamMemberSuccessAction({ ...data }));
  } catch (e) {
    yield put(inviteTeamMemberErrorAction(e.message));
  }
}

function* wasUserAddedInActiveSessionHandler() {
  try {
    yield put(wasUserAddedInActiveSessionLoadingAction());
    yield put(wasUserAddedInActiveSessionSuccessAction());
  } catch (e) {
    yield put(wasUserAddedInActiveSessionErrorAction(e.message));
  }
}

function* getMapFieldsHandler() {
  try {
    yield put(getMapFieldsLoadingAction());

    const data: GetMapFieldsSchema = yield httpGet("/team/saml/mapfields");

    yield put(getMapFieldsSuccessAction({ ...data }));
  } catch (e) {
    yield put(getMapFieldsErrorAction(e.message));
  }
}

function* getSamlConfigHandler(appName: string) {
  try {
    yield put(getSamlConfigLoadingAction());

    const data: SamlConfigSchema = yield httpGet("/team/saml", {
      params: appName
    });

    yield put(getSamlConfigSuccessAction({ ...data, queriedAppName: appName }));
  } catch (e) {
    yield put(getSamlConfigErrorAction(e.message));
  }
}

function* postSamlConfigHandler(payload: { body: any; appName: string }): any {
  try {
    yield put(postSamlConfigLoadingAction());

    const data: any = yield httpPost("/team/saml", {}, payload.body);
    yield put(getSamlConfigAction(payload.appName));
    if (data["ErrorCode"]) {
      throw new Error(data["Description"]);
    }

    yield put(postSamlConfigSuccessAction({ ...data }));
    Toastify("success", "Single Sign-on configuration has been added.");
  } catch (e) {
    yield put(postSamlConfigErrorAction(e.message));
    Toastify("error", e.message || "Something went wrong. Please try again.");
  }
}

function* putSamlConfigHandler(payload: { body: any; appName: string }): any {
  try {
    yield put(putSamlConfigLoadingAction());

    const data: any = yield httpPut("/team/saml", {}, payload.body);
    yield put(getSamlConfigAction(payload.appName));
    if (data["ErrorCode"]) {
      throw new Error(data["Description"]);
    }
    yield put(putSamlConfigSuccessAction({ ...data }));
    Toastify("success", "Single Sign-on configuration has been updated.");
    yield put(getSamlConfigAction(payload.appName));
  } catch (e) {
    yield put(putSamlConfigErrorAction(e.message));
    Toastify("error", e.message || "Something went wrong. Please try again.");
  }
}

function* deleteSamlConfigHandler(payload: { provider: string; appName: string }) {
  const samlPayload = {
    data: [
      {
        status: false,
        feature: "only_sso_login_enabled"
      }
    ]
  };
  try {
    yield put(deleteSamlConfigLoadingAction());

    const data: DeleteSamlConfigSchema = yield httpDelete("/team/saml", {}, { Provider: payload.provider });
    yield put(deleteSamlConfigSuccessAction({ ...data }));
    yield put(setAppFeaturesAction(samlPayload));
    Toastify("success", "Single Sign-on configuration has been deleted.");
    yield put(getSamlConfigAction(payload.appName));
  } catch (e) {
    yield put(deleteSamlConfigErrorAction(e.message));
    Toastify("error", "Something went wrong. Please try again.");
  }
}

function* transferAccountHandler(transferData: any) {
  try {
    yield put(transferAccountLoadingAction());

    const data: TransferAccountSchema = yield httpPost("/team/transfer", {}, transferData);

    yield put(transferAccountSuccessAction({ ...data }));
  } catch (e) {
    yield put(transferAccountErrorAction(e.message));
  }
}

function* updateCurrentUserHandler(): any {
  try {
    yield put(updateCurrentUserLoadingAction());
    const data: any = yield httpPut("/team/currentuser", {}, { role: ["Admin"] });
    yield put(updateCurrentUserSuccessAction(data));
  } catch (e) {
    yield put(updateCurrentUserErrorAction(e.message));
  }
}

/**
 * Watchers
 */

export function* watchGetTeamUsers() {
  while (true) {
    yield take(TeamConst.GET_TEAM_USERS);
    yield call(getTeamUsersHandler);
  }
}

export function* watchGetTeamOwner() {
  while (true) {
    yield take(TeamConst.GET_TEAM_OWNER);
    yield call(getTeamOwnerHandler);
  }
}

export function* watchDeleteTeamUser() {
  while (true) {
    const {
      payload: { uid }
    } = yield take(TeamConst.DELETE_TEAM_USER);
    yield call(deleteTeamUserHandler, uid);
  }
}

export function* watchDeleteSelf() {
  while (true) {
    const {
      payload: { appID, appName }
    } = yield take(TeamConst.DELETE_SELF);
    yield call(deleteSelfHandler, appID, appName);
  }
}

export function* watchResendTeamUserPassword() {
  while (true) {
    const {
      payload: { email, template, url }
    } = yield take(TeamConst.RESEND_TEAM_USER_PASSWORD);
    yield call(resendTeamUserPasswordHandler, email, template, url);
  }
}

export function* watchLogOutTeamUser() {
  while (true) {
    const {
      payload: { uid }
    } = yield take(TeamConst.LOG_OUT_TEAM_USER);
    yield call(logOutTeamUserHandler, uid);
  }
}

export function* watchGetCurrentUser() {
  while (true) {
    yield take(TeamConst.GET_CURRENT_USER);
    yield call(getCurrentUserHandler);
  }
}

export function* watchGetAppRoles() {
  while (true) {
    yield take(TeamConst.GET_APP_ROLES);
    yield call(getAppRolesHandler);
  }
}

export function* watchUpdateTeamUserRole() {
  while (true) {
    const {
      payload: { email, roles, additionalPermissions }
    } = yield take(TeamConst.UPDATE_TEAM_USER_ROLE);
    yield call(updateTeamUserRoleHandler, email, roles, additionalPermissions);
  }
}

export function* watchMapFields() {
  while (true) {
    yield take(TeamConst.GET_MAP_FIELDS);
    yield call(getMapFieldsHandler);
  }
}

export function* watchSamlConfig(): any {
  while (true) {
    const data = yield take(TeamConst.GET_SAML_CONFIG);
    yield call(getSamlConfigHandler, data.payload);
  }
}

export function* watchPostSamlConfig(): any {
  while (true) {
    const data = yield take(TeamConst.POST_SAML_CONFIG);
    yield call(postSamlConfigHandler, data.payload);
  }
}

export function* watchPutSamlConfig(): any {
  while (true) {
    const data = yield take(TeamConst.PUT_SAML_CONFIG);
    yield call(putSamlConfigHandler, data.payload);
  }
}

export function* watchDeleteSamlConfig(): any {
  while (true) {
    const data = yield take(TeamConst.DELETE_SAML_CONFIG);
    yield call(deleteSamlConfigHandler, data.payload);
  }
}

export function* watchAuditLogs(): any {
  while (true) {
    const data = yield take(TeamConst.GET_AUDIT_LOGS);
    yield call(getAuditLogsHandler, data.payload.cursor);
  }
}

export function* watchTeamAuditLogsV2(): any {
  while (true) {
    const { payload } = yield take(TeamConst.GET_TEAM_AUDIT_LOGS_V2);
    yield call(getTeamAuditLogsV2Handler, payload);
  }
}

export function* watchRoleList() {
  while (true) {
    yield take(TeamConst.GET_ROLE_LIST);
    yield call(getRoleListHandler);
  }
}

export function* watchOwnerInfo() {
  while (true) {
    yield take(TeamConst.GET_OWNER_INFO);
    yield call(getOwnerInfoHandler);
  }
}

export function* watchInviteTeamMember(): any {
  while (true) {
    const data = yield take(TeamConst.INVITE_TEAM_MEMBER);
    yield call(inviteTeamMemberHandler, data.payload);
  }
}

export function* watchTransferAccount(): any {
  while (true) {
    const data = yield take(TeamConst.TRANSFER_ACCOUNT);
    yield call(transferAccountHandler, data.payload);
  }
}

export function* watchUpdateCurrentUser() {
  while (true) {
    yield take(TeamConst.UPDATE_CURRENT_USER);
    yield call(updateCurrentUserHandler);
  }
}

export function* watchWasUserAddedInActiveSession() {
  while (true) {
    yield take(TeamConst.WAS_USER_ADDED_IN_ACTIVE_SESSION);
    yield call(wasUserAddedInActiveSessionHandler);
  }
}
